import axios from 'axios';
import queryString from 'query-string';
import { omit, isString, isNil } from 'lodash';
import { get } from 'lodash/fp';
import {
  BasicPivotItem,
  ListDataOptions,
  Pivot,
  ListFitViewOptions,
  ValidMembersQuery,
  RegionItem,
  OverTimeQuery,
  OverTimeType,
  TimeQuery,
  TimeSheetInfo,
  TimeEntry,
  Response,
  FlowSheetByIndexQuery,
  WorklistInfo,
  PivotServiceType,
  FitViewOptions,
} from './pivotWorker.types';
import { AppType } from 'configuration-schemas/general';
import { buildFlatDataArr } from './pivotWorker.utils';
import { BehaviorSubject, map, filter, firstValueFrom } from 'rxjs';
import { streamParseJof } from './deserializer';

const validMembersPath = 'api/member/validMembers';
const flowSheetPath = 'api/v2/flowsheet';
const pricingPath = 'api/v2/overtime';
const timeMappingPath = 'api/assortment/timeMapping';
const completeListDataPath = '/asst/api/pivot3/listData';
const completeFitViewPath = '/asst/api/pivot2/fitView';
const completeFlowSheetByIndexPath = '/asst/api/v2/flowsheetall';

const accessToken$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

const availableAccessToken$ = accessToken$.pipe(filter((token) => !!token)).pipe(map((token) => token));

const Axios = axios.create({
  baseURL: '/asst',
});

Axios.interceptors.request.use(async (config) => {
  const token = await firstValueFrom(availableAccessToken$);
  const headers = config.headers || {};
  headers.authorization = `Bearer ${token}`;
  return config;
});

async function generateFetchRequest(url: string, method: 'GET' | 'POST' = 'GET', body?: any, contentType?: string) {
  const token = await firstValueFrom(availableAccessToken$);
  const headers = new Headers();
  headers.append('Authorization', `Bearer ${token}`);
  if (method === 'POST' && contentType) {
    headers.append('Content-Type', contentType);
  }
  return new Request(url, {
    method: method,
    body,
    cache: 'no-cache',
    headers,
  });
}

/* Send an access token into the worker
 */
export function receiveAccessToken(type: PivotServiceType, token: string | null) {
  accessToken$.next(token);
}

export async function getListFitViewData(
  appName: AppType,
  defnId: string,
  options: FitViewOptions | ListFitViewOptions,
): Promise<Pivot> {
  const pivotQuery = {
    defnId,
    appName,
    ...options,
  };
  const paramsStr = queryString.stringify(omit(pivotQuery, 'cancelToken'));
  const url = `${completeFitViewPath}?${paramsStr}`;
  let reader: ReadableStreamDefaultReader<string> | undefined;
  try {
    const req = await generateFetchRequest(url);
    const res = await fetch(req);
    if (!res.ok) {
      throw new Error(`An error was encountered. Details: getListFitViewData response failed with code: ${res.status}`);
    }
    const body = res.body;
    if (!body) {
      throw new Error(`An error was encountered. Details: getListFitViewData response had no body.`);
    }

    const decoded = body.pipeThrough(new TextDecoderStream());
    reader = decoded.getReader();

    const formattedResponse = (await streamParseJof(reader)) as BasicPivotItem[];
    const flatData = buildFlatDataArr(formattedResponse);

    return {
      flat: flatData,
      tree: formattedResponse,
    };
  } catch (error) {
    console.error((error as Error).message); // eslint-disable-line no-console
    throw error;
  } finally {
    if (!isNil(reader)) {
      reader.releaseLock();
    }
  }
}

/**
 *
 * @param appName
 * @param defnId
 * @param options
 * @param formatAsPivotType an optional flag that allows certain pivot response to be left in BasicPivotItem[] format
 * @returns
 */
export async function getListData(appName: AppType, defnId: string, options: ListDataOptions): Promise<Pivot> {
  const pivotQuery = {
    defnId,
    appName,
    ...options,
  };
  const paramsStr = queryString.stringify(pivotQuery);
  // We have to prefix by /asst since we are using fetch rather than axios
  const url = `${completeListDataPath}?${paramsStr}`;
  let reader: ReadableStreamDefaultReader<string> | undefined;
  try {
    const req = await generateFetchRequest(url);
    const res = await fetch(req);
    if (!res.ok) {
      throw new Error(`An error was encountered. Details: getListData response failed with code: ${res.status}`);
    }
    const body = res.body;
    if (!body) {
      throw new Error(`An error was encountered. Details: getListData response had no body.`);
    }

    const decoded = body.pipeThrough(new TextDecoderStream());
    reader = decoded.getReader();
    const unformattedResponse = (await streamParseJof(reader)) as BasicPivotItem[];
    const flatData = buildFlatDataArr(unformattedResponse);

    return {
      flat: flatData,
      tree: unformattedResponse,
    };
  } catch (error) {
    console.error((error as Error).message); // eslint-disable-line no-console
    throw error;
  } finally {
    if (!isNil(reader)) {
      reader.releaseLock();
    }
  }
}

export async function getValidMembers(memberQuery: ValidMembersQuery): Promise<RegionItem[]> {
  const paramsStr = queryString.stringify(memberQuery);
  const response = await Axios.get(`${validMembersPath}?${paramsStr}`);
  return isString(response.data) ? JSON.parse(response.data).data : response.data.data;
}

export async function getOverTimeData(overTimeQuery: OverTimeQuery, type: OverTimeType): Promise<Pivot> {
  const paramsStr = queryString.stringify(overTimeQuery);
  const path = type === OverTimeType.flowsheet ? flowSheetPath : pricingPath;
  const completeUrl = `/asst/${path}?${paramsStr}`;
  let reader: ReadableStreamDefaultReader<string> | undefined;
  try {
    const req = await generateFetchRequest(completeUrl);
    const res = await fetch(req);
    if (!res.ok) {
      throw new Error(`An error was encountered. Details: getOverTimeData response failed with code: ${res.status}`);
    }
    const body = res.body;
    if (!body) {
      throw new Error(`An error was encountered. Details: getOverTimeData response had no body.`);
    }

    const decoded = body.pipeThrough(new TextDecoderStream());
    reader = decoded.getReader();
    const formattedResponse = (await streamParseJof(reader)) as BasicPivotItem[];
    const flatData = buildFlatDataArr(formattedResponse);

    return {
      flat: flatData,
      tree: formattedResponse,
    };
  } catch (error) {
    console.error((error as Error).message); // eslint-disable-line no-console
    throw error;
  } finally {
    if (!isNil(reader)) {
      reader.releaseLock();
    }
  }
}

export async function getWorklistData(): Promise<WorklistInfo[]> {
  const path = 'api/worklist/';
  const response = await Axios.get<string>(`${path}`);
  return (response.data as any).data;
}

export async function getFlowSheetByIndex(flowsheetQuery: FlowSheetByIndexQuery): Promise<Pivot> {
  const url = `${completeFlowSheetByIndexPath}`;
  let reader: ReadableStreamDefaultReader<string> | undefined;
  try {
    const req = await generateFetchRequest(url, 'POST', JSON.stringify(flowsheetQuery), 'application/json');
    const res = await fetch(req);
    if (!res.ok) {
      throw new Error(
        `An error was encountered. Details: getFlowSheetByIndex response failed with code: ${res.status}`,
      );
    }
    const body = res.body;
    if (!body) {
      throw new Error(`An error was encountered. Details: getFlowSheetByIndex response had no body.`);
    }

    const decoded = body.pipeThrough(new TextDecoderStream());
    reader = decoded.getReader();

    const formattedResponse = (await streamParseJof(reader)) as BasicPivotItem[];
    const flatData = buildFlatDataArr(formattedResponse);

    return {
      flat: flatData,
      tree: formattedResponse,
    };
  } catch (error) {
    console.error((error as Error).message); // eslint-disable-line no-console
    throw error;
  } finally {
    if (!isNil(reader)) {
      reader.releaseLock();
    }
  }
}

export async function getTimeMapping(query: TimeQuery): Promise<TimeSheetInfo> {
  const paramsStr = queryString.stringify(query);
  const response = await Axios.get<Response<TimeEntry[]>>(`${timeMappingPath}?${paramsStr}`);
  return {
    entries: response.data.data,
    planCurrent: get('data.info.plan_current.id', response),
  };
}
